import { Component, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { IDetailDocumentVerification } from '../../../../interfaces/detail-document-verification.interface';
import { IResponseApprovalDistributor, IVerificationDocumentKUL } from '../../../../interfaces/detail-verification.interface';
import { ICardVerificationForm, INoteChangeVerification, IPayloadRegistrationVerification } from '../../../../interfaces/detail-registration.interface';
import { DistributorService } from '../../../../distributor.service';
import { DetailRegistrationDistributorService } from '../../detail-registration/detail-registration.service';
import { EnumCardVerificationForm, EnumCardVerificationSection, EnumTypeBodyCardVerificationForm } from '../../detail-registration/distributor-data/distributor-data.data';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { EnumVerificationStatus } from '@pages/sales-marketing/program-marketing-legacy/program-marketing.enum';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { UtilitiesService } from '@services/utilities.service';
import { DistributorStatusEnum } from '@config/enum/distributor.enum';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';

enum EnumLabelKeyFormString {
  document_verify_kul = 'Dokumen Verifikasi',
}

@Component({
  selector: 'app-verification-tab-informasi-kul',
  templateUrl: './verification-tab-informasi-kul.component.html',
  styleUrls: ['./verification-tab-informasi-kul.component.scss'],
})
export class VerificationTabInformasiKULComponent implements OnInit {
  @ViewChild('modalConfirmVerification') modalConfirmVerification: ModalComponent;
  modalConfigConfirmVerification = <ModalConfig>{
    modalTitle: 'Konfirmasi Aktivasi Distributor',
    dismissButtonLabel: 'Lanjutkan',
    closeButtonLabel: 'Batal',
    onDismiss: () => this.modalConfirmVerification.close().then(() => this.onSubmitVerification()),
  };

  @ViewChild('modalResponseVerification') modalResponseVerification: ModalComponent;
  modalConfigResponse = <ModalConfig>{
    showHeader: true,
    dismissButtonLabel: 'Oke',
    closeButtonLabel: 'Lihat Detail',
    hideCloseButton: () => false,
    disableDismissButton: () => this.loadingSubmit.value,
    disableCloseButton: () => this.loadingSubmit.value,
    onDismiss: () => this.onDismissModalResponse(),
    onClose: () => this.onCloseModalResponse(),
  };

  isLoading = new BehaviorSubject(false);
  snapshotParams!: Params;

  // init declare object session
  revisionNoteData = { revision_note: { document_verify_kul: null } } as IPayloadRegistrationVerification;

  dataResponse = new BehaviorSubject({} as IDetailDocumentVerification<IVerificationDocumentKUL>);
  bodyDataDocument = new BehaviorSubject([] as ICardVerificationForm[]);

  loadingSubmit = new BehaviorSubject(false);
  dataModalResponse = {} as IResponseApprovalDistributor;

  form = this.fb.group({
    document_verify_kul: ['', Validators.required],
  });

  get docVerifyControl() {
    return <FormControl>this.form.get('document_verify_kul');
  }

  get sectionVerificationStatus() {
    return this.dataResponse.value?.documents?.status_verified_enum;
  }

  get detailHeader() {
    return this.distributorService.DistributorDetailHeader;
  }

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private distributorService: DistributorService,
    private registrationService: DetailRegistrationDistributorService,
    private utils: UtilitiesService
  ) {}

  ngOnInit(): void {
    this.snapshotParams = this.activatedRoute.snapshot.params;
    this.getRevisionNoteData();
    this.getDetailVerificationKUL();
  }

  getDetailVerificationKUL() {
    this.isLoading.next(true);
    this.distributorService.getDetailVerificationInformasiKUL(this.snapshotParams.id).subscribe((res) => {
      if (!res) return;
      this.dataResponse.next(res && res.data);
      this.generateDocumentBodyData();
      this.handleValidationForm();
      this.isLoading.next(false);
    });
  }

  generateDocumentBodyData() {
    const { documents } = this.dataResponse.value;
    const _documentBody = this.getDocumentKulBody();
    const _documents = this.staticMapperDataCard(documents);
    this.bodyDataDocument.next(this.registrationService.generateBodyData(_documents, _documentBody));
  }

  getDocumentKulBody() {
    return <ICardVerificationForm[]>[
      { label: 'KUL', type: EnumTypeBodyCardVerificationForm.IMG_URL, key: 'kul_document_url' },
      { label: 'Foto TTD KUL', type: EnumTypeBodyCardVerificationForm.IMG_URL, key: 'signing_kul_url' },
      { label: 'Pakta Integritas', type: EnumTypeBodyCardVerificationForm.IMG_URL, key: 'integrity_pact_url' },
    ];
  }

  staticMapperDataCard<T>(data: T) {
    return {
      revision_note: '',
      revision_data: null,
      current_data: data,
    };
  }

  onSubmitVerification() {
    const _payload = this.revisionNoteData;
    this.loadingSubmit.next(true);
    this.registrationService.submitRegistrationAdmin(_payload).subscribe((resp) => {
      if (!resp) return;
      this.dataModalResponse = resp && resp.data;
      this.loadingSubmit.next(false);
    });
    return this.modalResponseVerification.open();
  }

  getRevisionNoteData() {
    this.revisionNoteData = this.registrationService.getSessionRevisionNote(EnumDistributorRevisionSession.VERIFY_KUL) ?? this.revisionNoteData;
  }

  onNoteChanges(e: INoteChangeVerification) {
    this.docVerifyControl.setValue(e.note);
    if (e && !e.note) return;
    this.registrationService.setRevisionData(this.revisionNoteData, e, EnumDistributorRevisionSession.VERIFY_KUL);
  }

  handleValidationForm() {
    if (this.dataHasLoaded() && this.hasRevisionSession()) {
      this.docVerifyControl.setValue(this.revisionNoteData.revision_note.document_verify_kul);
    } else {
      this.resetRevisionData();
    }

    this.form.updateValueAndValidity();
  }

  afterActionVerification(e: string | null) {
    // if null - reset form && remove session
    if (e === null) {
      this.removeSessionRevisionNote();
      this.handleValidationForm();
    }

    setTimeout(() => this.getDetailVerificationKUL());
  }

  isConfirmValid() {
    if (this.sectionVerificationStatus && this.sectionVerificationStatus === EnumVerificationStatus.VERIFIED) return true;
    else return this.form.valid;
  }

  resetRevisionData() {
    this.form.reset();
    this.revisionNoteData.revision_note.document_verify_kul = this.docVerifyControl.value;
  }

  removeSessionRevisionNote = () => this.registrationService.clearSessionRevisionNote(EnumDistributorRevisionSession.VERIFY_KUL);

  noteValue() {
    if (this.hasRevisionSession()) return this.revisionNoteData.revision_note.document_verify_kul as string;
    return '';
  }

  hasRevisionSession = () =>
    !!(
      this.registrationService.getSessionRevisionNote(EnumDistributorRevisionSession.VERIFY_KUL) &&
      Object.keys(this.registrationService.getSessionRevisionNote(EnumDistributorRevisionSession.VERIFY_KUL)).length
    );

  dataHasLoaded = () => this.dataResponse.value && Object.keys(this.dataResponse.value).length > 0;

  onDismissModalResponse() {
    this.router.navigate(['/distributor/pending/verification']).then();
  }

  onCloseModalResponse() {
    const { distributor_id, distributor_status_enum } = this.dataModalResponse;
    const _status = this.utils.mapKeyToString(DistributorStatusEnum, distributor_status_enum).toLowerCase() as string;
    const _detailUrl = this.hasRevisionNote() ? `/distributor/detail/reject/verification/${distributor_id}` : `/distributor/detail/teregistrasi/${_status}/${distributor_id}`;
    this.removeSessionRevisionNote();
    this.router.navigate([_detailUrl]).then(() => window.location.reload());
  }

  copyToClipboard(data = this.dataModalResponse) {
    const { distributor_email, distributor_password } = data;
    const _text = distributor_email + '\n' + distributor_password;
    return this.utils.copyToClipboard(_text);
  }

  hasRevisionNote = () => this.revisionNoteData && Object.values(this.revisionNoteData.revision_note).some((val) => val !== null && val !== '');

  getRevisionNoteDataBody() {
    const { revision_note } = this.revisionNoteData;
    return Object.entries(revision_note)
      .map(([key, value]) => {
        return {
          label: this.utils.mapKeyToString(EnumLabelKeyFormString, key),
          value: value,
        };
      })
      .filter((item) => item.value !== null && item.value !== '');
  }

  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
